import { Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Badge } from '@/components/ui/badge'
import { formatMs } from '@/features/admin/processing-format'
import type { ProcessingActiveJob } from '@/services/api/adminProcessing'

function Pair({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex min-w-0 items-baseline justify-between gap-2">
      <dt className="shrink-0 text-muted-foreground">{label}</dt>
      <dd className={mono ? 'truncate font-mono' : 'truncate tabular-nums'}>{value}</dd>
    </div>
  )
}

/**
 * Phone layout of the active-jobs table: one block per job, so the running step and the timings
 * are on screen instead of three columns to the right of it.
 */
export function ActiveJobsMobileList({ jobs }: { jobs: ProcessingActiveJob[] }) {
  const { t } = useTranslation()
  return (
    <ul className="divide-y divide-border md:hidden">
      {jobs.map(j => (
        <li key={j.job_id} className="space-y-1.5 px-2 py-2.5">
          <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1">
            <span className="min-w-0 text-sm font-medium wrap-anywhere">{j.pipeline}</span>
            {j.current_step && (
              <Badge variant="secondary" className="max-w-full min-w-0 shrink gap-1">
                <Loader2 className="h-3 w-3 shrink-0 animate-spin" />
                <span className="truncate">{j.current_step}</span>
              </Badge>
            )}
          </div>
          <dl className="grid grid-cols-1 gap-x-4 gap-y-1 text-xs sm:grid-cols-2">
            <Pair label={t('admin.processing.cols.job')} value={j.job_id.slice(0, 8)} mono />
            <Pair
              label={t('admin.processing.activeJobs.colDocument')}
              value={j.document_id?.slice(0, 8) ?? '—'}
              mono
            />
            <Pair
              label={t('admin.processing.activeJobs.colElapsed')}
              value={formatMs(j.elapsed_ms)}
            />
            <Pair label={t('admin.processing.activeJobs.colEta')} value={formatMs(j.eta_ms)} />
          </dl>
        </li>
      ))}
    </ul>
  )
}
